import React from "react";
import PropTypes from "prop-types";
import {
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  Select,
  MenuItem,
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import { debounce } from "throttle-debounce";
import { translate as t } from "../../../../../../../../../infrastructure/translate/localization";
import Table from "../../../../../../../Table";
import KeepRecoverRadioGroup from "../KeepRecoverRadioGroup";
import { MonetaryAmount } from "../../../../../../../../domain/monetary-amount";
import displayEmployeesMassiveDeleteDeliveredBalances from "../../../../../../../../../usecases/employees/displays/display-employees-massive-delete-delivered-balances-table";
import useStyles, { CustomInput } from "./styles";
import { CustomTextField } from "../../../../components/StepHeader/styles";
import EmptyDataForTableMessage from "../../../../components/EmptyDataForTableMessage";
import TableDataSpinner from "../../../../components/TableDataSpinner";
import StepFooter from "../../../../components/StepFooter";
import deleteMultipleEmployees from "../../../../../../../../../usecases/employees/delete-multiple-employees";

const columns = [
  { id: "checkbox", label: "" },
  { id: "legalId", label: t("common-messages.nif-nie") },
  { id: "nameAndSurname", label: t("common-messages.name-and-surname") },
  {
    id: "deliveredFlexBalance",
    label: t("employees-section.delivered-flex-balance"),
    align: "center",
  },
  {
    id: "deliveredSubsidyBalance",
    label: t("employees-section.delivered-subsidy-balance"),
    align: "center",
  },
];

const modProperty = ({ data, value, property, employeeId, mustBeChecked }) => {
  if (employeeId) {
    return data.map((item) => {
      if (employeeId === item.employeeId) {
        return { ...item, [property]: value };
      }
      return item;
    });
  }
  if (mustBeChecked) {
    return data.map((item) => {
      return { ...item, [property]: item.checked ? value : item[property] };
    });
  }

  return data.map((item) => {
    return { ...item, [property]: value };
  });
};

export default function DeliveredBalancesTable({
  data,
  currentFlexSelection,
  currentSubsidySelection,
  currentSearch,
  isLoading,
  ...rest
}) {
  const {
    modifierContainer,
    checkbox,
    searchButton,
    searchContainer,
    searchIcon,
  } = useStyles();

  const handleSubmit = () => {
    const payload = {
      operations: data.map(({ employeeId, flex, subsidy }) => ({
        employeeId,
        accumulated: flex || "keep",
        allowance: subsidy || "keep",
      })),
    };

    deleteMultipleEmployees(payload);
  };

  const handleSearch = debounce(1000, false, (value) => {
    displayEmployeesMassiveDeleteDeliveredBalances({
      data,
      currentFlexSelection,
      currentSubsidySelection,
      currentSearch: value,
    });
  });

  const filterData = (items) => {
    if (currentSearch !== undefined) {
      if (currentSearch !== "") {
        return items.filter(
          ({ employee }) =>
            employee?.legalId
              ?.toLowerCase()
              .includes(currentSearch.toLowerCase()) ||
            employee?.name
              ?.toLowerCase()
              .includes(currentSearch.toLowerCase()) ||
            employee?.surname
              ?.toLowerCase()
              .includes(currentSearch.toLowerCase())
        );
      }
    }
    return items;
  };

  return (
    <>
      {(!currentSearch || data.length > 0) && !isLoading && (
        <Grid
          item
          container
          xs={12}
          className={searchContainer}
          alignItems="center"
        >
          <CustomTextField
            variant="outlined"
            defaultValue={currentSearch || ""}
            onChange={(e) => handleSearch(e.target.value)}
            size="small"
          />
          <Button
            className={searchButton}
            onClick={() => {}}
            startIcon={<SearchIcon className={searchIcon} />}
          >
            {t("actions.search")}
          </Button>
        </Grid>
      )}
      {isLoading ? (
        <TableDataSpinner />
      ) : (
        <>
          <Grid
            item
            container
            xs={12}
            alignItems="center"
            className={modifierContainer}
          >
            <FormControlLabel
              control={
                <Checkbox
                  className={checkbox}
                  checked={
                    data.reduce((acc, curr) => curr?.checked && acc, true) ||
                    false
                  }
                  onChange={(e) =>
                    displayEmployeesMassiveDeleteDeliveredBalances({
                      data: modProperty({
                        data,
                        value: e.target.checked,
                        property: "checked",
                      }),
                      currentSearch,
                    })
                  }
                  name="all-are-checked"
                  disableRipple
                />
              }
              label={t("common-messages.select-all", { num: data.length })}
            />
            {data.some(({ checked }) => checked) && (
              <>
                <Select
                  input={<CustomInput />}
                  value={currentFlexSelection || "none"}
                  onChange={(e) =>
                    displayEmployeesMassiveDeleteDeliveredBalances({
                      data: modProperty({
                        data,
                        value: e.target.value,
                        property: "flex",
                        mustBeChecked: true,
                      }),
                      currentFlexSelection: e.target.value,
                      currentSubsidySelection,
                      currentSearch,
                    })
                  }
                >
                  <MenuItem value="none" disabled>
                    {t("employees-section.delivered-flex-balance")}
                  </MenuItem>
                  <MenuItem value="recover">{`${t("actions.recover")} ${t(
                    "common-messages.balance"
                  ).toLowerCase()}`}</MenuItem>
                  <MenuItem value="keep">{`${t("actions.keep")} ${t(
                    "common-messages.balance"
                  ).toLowerCase()}`}</MenuItem>
                </Select>
                <Select
                  input={<CustomInput />}
                  value={currentSubsidySelection || "none"}
                  onChange={(e) =>
                    displayEmployeesMassiveDeleteDeliveredBalances({
                      data: modProperty({
                        data,
                        value: e.target.value,
                        property: "subsidy",
                        mustBeChecked: true,
                      }),
                      currentFlexSelection,
                      currentSubsidySelection: e.target.value,
                      currentSearch,
                    })
                  }
                >
                  <MenuItem value="none" disabled>
                    {t("employees-section.delivered-subsidy-balance")}
                  </MenuItem>
                  <MenuItem value="recover">{`${t("actions.recover")} ${t(
                    "common-messages.balance"
                  ).toLowerCase()}`}</MenuItem>
                  <MenuItem value="keep">{`${t("actions.keep")} ${t(
                    "common-messages.balance"
                  ).toLowerCase()}`}</MenuItem>
                </Select>
              </>
            )}
          </Grid>
          {filterData(data).length > 0 ? (
            <Table
              columns={columns}
              data={filterData(data).map(
                ({
                  employee,
                  employeeId,
                  totalAccumulated,
                  totalFunds,
                  checked = false,
                  flex = "keep",
                  subsidy = "keep",
                }) => {
                  return {
                    checkbox: (
                      <Checkbox
                        className={checkbox}
                        checked={checked}
                        onChange={(e) => {
                          displayEmployeesMassiveDeleteDeliveredBalances({
                            data: modProperty({
                              data,
                              value: e.target.checked,
                              property: "checked",
                              employeeId,
                            }),
                            currentFlexSelection,
                            currentSubsidySelection,
                            currentSearch,
                          });
                        }}
                        disableRipple
                      />
                    ),
                    legalId: employee.legalId,
                    nameAndSurname: `${employee.name} ${employee.surname}`,
                    deliveredFlexBalance: (
                      <KeepRecoverRadioGroup
                        title={`${new MonetaryAmount(
                          totalAccumulated.amountInCents
                        ).prettifyMoney({})}`}
                        onChange={(e) => {
                          displayEmployeesMassiveDeleteDeliveredBalances({
                            data: modProperty({
                              data,
                              value: e.target.value,
                              property: "flex",
                              employeeId,
                            }),
                            currentSearch,
                          });
                        }}
                        value={flex}
                      />
                    ),
                    deliveredSubsidyBalance: (
                      <KeepRecoverRadioGroup
                        title={`${new MonetaryAmount(
                          totalFunds.amountInCents
                        ).prettifyMoney({})}`}
                        employeeId={employeeId}
                        onChange={(e) => {
                          displayEmployeesMassiveDeleteDeliveredBalances({
                            data: modProperty({
                              data,
                              value: e.target.value,
                              property: "subsidy",
                              employeeId,
                            }),
                            currentSearch,
                          });
                        }}
                        value={subsidy}
                      />
                    ),
                  };
                }
              )}
              {...rest}
            />
          ) : (
            <EmptyDataForTableMessage
              message={`${t(
                "common-messages.empty-data-for-selected-employees",
                {
                  dataName: t(
                    "employees-section.delivered-balances"
                  ).toLowerCase(),
                }
              )}.`}
            />
          )}
        </>
      )}
      <StepFooter
        isLoading={isLoading}
        employees={data}
        overrideLabel={t("employees-section.delete-employees")}
        overrideAction={handleSubmit}
      />
    </>
  );
}

DeliveredBalancesTable.defaultProps = {
  data: [],
  currentFlexSelection: undefined,
  currentSubsidySelection: undefined,
  currentSearch: undefined,
  isLoading: false,
};

DeliveredBalancesTable.propTypes = {
  data: PropTypes.array,
  currentFlexSelection: PropTypes.string,
  currentSubsidySelection: PropTypes.string,
  currentSearch: PropTypes.string,
  isLoading: PropTypes.bool,
};
